import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import useUserStore from './store/userStore';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import LearnerDashboard from './pages/Learner/Dashboard';
import TrainerDashboard from './pages/Trainer/Dashboard';
import PolicymakerDashboard from './pages/Policymaker/Dashboard';
import Profile from './pages/Profile/Profile';
import Courses from './pages/Courses/Courses';
import Jobs from './pages/Jobs/Jobs';
import Skills from './pages/Skills/Skills';
import Analytics from './pages/Analytics/Analytics';
import HistoryPage from './pages/History/History';
import ChatView from './pages/History/ChatView';
import LoadingSpinner from './components/UI/LoadingSpinner';

function App() {
  // Always call hooks first - React requirement
  const { isAuthenticated, isLoading, user, refreshToken, token } = useAuthStore();
  const { profile, updateProfile, isLoading: userLoading, fetchProfile } = useUserStore();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Check for demo mode AFTER calling hooks
  const demoMode = process.env.REACT_APP_DEMO_MODE === 'true';

  useEffect(() => {
    const { token } = useAuthStore.getState();
    // Try to refresh token on app start
    if (token && !isAuthenticated) {
      refreshToken();
    }
  }, [isAuthenticated, refreshToken]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchProfile();
    }
  }, [isAuthenticated, fetchProfile, token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (demoMode) {
    // Demo mode - bypass auth
    return (
      <Router>
        <Layout toggleTheme={toggleTheme} theme={theme} >
          <Routes>
            <Route path="/" element={<Navigate to="/learner" replace />} />
            <Route path="/learner" element={<LearnerDashboard />} />
            <Route path="/trainer" element={<TrainerDashboard />} />
            <Route path="/policymaker" element={<PolicymakerDashboard />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/skills" element={<Skills />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/history/:sessionId" element={<ChatView />} />
            <Route path="*" element={<Navigate to="/learner" replace />} />
          </Routes>
        </Layout>
      </Router>
    );
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  const getDashboardRoute = () => {
    switch (user?.user_type) {
      case 'learner':
        return '/learner';
      case 'trainer':
        return '/trainer';
      case 'policymaker':
        return '/policymaker';
      default:
        return '/learner';
    }
  };

  return (
    <Router>
      <Layout toggleTheme={toggleTheme} theme={theme} >
        <Routes>
          <Route path="/" element={<Navigate to={getDashboardRoute()} replace />} />

          {/* Learner Routes */}
          <Route path="/learner">
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<LearnerDashboard />} />

            <Route path="courses" element={<Courses />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="skills" element={<Skills />} />
          </Route>

          {/* Trainer Routes */}
          <Route path="/trainer">
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<TrainerDashboard />} />
            <Route path="courses" element={<Courses />} />
            <Route path="analytics" element={<Analytics />} />
          </Route>

          {/* Policymaker Routes */}
          <Route path="/policymaker">
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<PolicymakerDashboard />} />
            <Route path="analytics" element={<Analytics />} />
          </Route>

          {/* Common Routes */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/history/:sessionId" element={<ChatView />} />

          <Route path="*" element={<Navigate to={getDashboardRoute()} replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;